/**
 * API utilities for making requests to the server
 */

import { toast } from 'react-hot-toast';

// Get API base URL from environment variables
const API_URL = import.meta.env.VITE_API_URL || 'https://chat-sphere-58as.onrender.com';

export const getApiUrl = () => {
  return API_URL;
};

/**
 * Make an API request with improved error handling
 * @param {string} endpoint - The API endpoint (without leading slash)
 * @param {object} options - Fetch options
 * @returns {Promise<object>} - The JSON response
 */
export const makeApiRequest = async (endpoint, options = {}) => {
  // Ensure endpoint starts with a slash and handle potential double slashes
  const formattedEndpoint = `/${endpoint.replace(/^\/+/, '')}`;
  const url = `${API_URL}${formattedEndpoint}`;
  
  // Set default headers if not provided
  if (!options.headers) {
    options.headers = {
      'Content-Type': 'application/json',
    };
  }
  
  try {
    console.log(`Making API request to: ${url}`);
    
    // Start timer to measure request duration
    const startTime = Date.now();
    
    // Make the fetch request
    const response = await fetch(url, options);
    
    // Calculate request duration
    const duration = Date.now() - startTime;
    console.log(`API request to ${endpoint} completed in ${duration}ms`);
    
    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('Received non-JSON response:', await response.text());
      throw new Error(`Received non-JSON response from server (${response.status})`);
    }
    
    // Parse JSON response
    const data = await response.json();
    
    // Check for error responses (even with 200 status)
    if (!response.ok) {
      const errorMessage = data.message || `HTTP error ${response.status}`;
      console.error('API error:', errorMessage, data);
      throw new Error(errorMessage);
    }
    
    return data;
  } catch (error) {
    // Enhance error with additional context
    if (error.message === 'Failed to fetch') {
      console.error(`Network error when connecting to ${url}`);
      toast.error('Network error: Could not connect to server');
      throw new Error(`Network error: Could not connect to server at ${API_URL}`);
    }
    
    console.error(`API request failed: ${error.message}`);
    toast.error(`API request failed: ${error.message}`);
    throw error;
  }
}; 